import { useMemo } from "react";
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  createPractice,
  deletePractice,
  getPractices,
  updatePractice,
} from "apps/project-iq/api";
import { IPractice } from "apps/project-iq/models/IPractice";

export const PRACTICES_QUERY_KEY = "practices";

export interface IUsePracticesResult {
  practices: IPractice[];
  isLoading: boolean;
  isError: boolean;
  createMutation: UseMutationResult<IPractice, Error, string>;
  updateMutation: UseMutationResult<IPractice, Error, IPractice>;
  deleteMutation: UseMutationResult<unknown, Error, string>;
}

export const usePractices = (): IUsePracticesResult => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<IPractice[], Error>({
    queryKey: [PRACTICES_QUERY_KEY],
    queryFn: () => getPractices(),
  });

  const practices = useMemo(() => data ?? [], [data]);

  const createMutation = useMutation<IPractice, Error, string>(
    (label: string) => createPractice({ label }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PRACTICES_QUERY_KEY]);
      },
    },
  );

  const updateMutation = useMutation<IPractice, Error, IPractice>(
    ({ id, label }: IPractice) => updatePractice(id, { label }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PRACTICES_QUERY_KEY]);
      },
    },
  );

  const deleteMutation = useMutation<unknown, Error, string>(
    (id: string) => deletePractice(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PRACTICES_QUERY_KEY]);
      },
    },
  );

  return {
    practices,
    isLoading,
    isError,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
